<script setup lang="ts">
import type { Page } from '~/utils/solr/literature'
import { faBooks } from '@fortawesome/pro-light-svg-icons'
import { PAGE_ID_QUERY } from '~/constants/urls'
import { cleanHighlights } from '~/utils/solr/highlighting-utils'
import { useTocState } from '~/composables/book/useTocState'
import { resolveNearestHeading } from '~/utils/literature/nearest-heading'

const props = defineProps<{
  doc: Page
  highlights?: string[] | null
}>()

const emit = defineEmits<{
  showSearchResult: [highlights: string[]]
}>()

const trimHighlightSnippet = useTrimHighlightSnippet()

function handleShowSearchResult() {
  emit('showSearchResult', cleanHighlights(props.highlights || []))
}

const pageId = useRouteQuery<string | undefined>(PAGE_ID_QUERY)

const activeHighlight = computed(() => {
  const currentPageId = pageId.value ? Number.parseInt(pageId.value) : null
  const docPageId = props.doc.pageNumberString ? Number.parseInt(props.doc.pageNumberString) : props.doc.pageCounter ?? null
  return docPageId != null && docPageId === currentPageId
})

const { flatTocItems } = useTocState()

const nearestHeading = computed(() => {
  return resolveNearestHeading({
    bodytext: props.doc.bodytext,
    fragment: props.highlights?.[0],
    tocItems: flatTocItems.value,
    pageNumberString: props.doc.pageNumberString,
  })
})

const accessibleLabel = computed(() => {
  return `Vis søkeresultat: ${nearestHeading.value ? `${nearestHeading.value}, ` : ''}side ${props.doc.pageNumberString}`
})
</script>

<template>
  <button
    v-if="doc.pageNumberString"
    :aria-label="accessibleLabel"
    class="group px-s pt-xs pb-s w-full hover:bg-surface-secondary dark:border-b-border-default hover:border-b-0 hover:rounded transition-all duration-300 ease-in-out"
    :class="activeHighlight ? 'active-highlight' : 'border-b-border-secondary border-b'"
    @click="handleShowSearchResult()"
  >
    <div class="flex flex-col gap-2xs my-xs search-result no-underline text-left w-full">
      <Chip size="small" variant="neutral" :icon-left="faBooks" color="carbon" class="text-caption !px-0">
        {{ `Side ${doc.pageNumberString || 'Ukjent'}` }}
      </Chip>
      <span v-if="nearestHeading" class="text-small font-bold text-primary line-clamp-2">
        {{ nearestHeading }}
      </span>
      <template v-if="highlights">
        <span
          v-for="(text, index) in highlights"
          :key="text + index"
          class="text-xsmall search-result-body"
          v-html="trimHighlightSnippet(text)"
        />
      </template>
      <template v-else-if="doc.bodytext">
        <span class="text-xsmall search-result-body line-clamp-2" v-html="doc.bodytext" />
      </template>
      <template v-else>
        <span class="text-xsmall text-carbon-70 italic">Ingen tekst på denne siden</span>
      </template>
    </div>
  </button>
</template>
